import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import {
  GridComponent,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Page,
  Filter,
  Toolbar,
  ExcelExport,
  Sort,
  Resize,
  Group,
  Freeze,
} from "@syncfusion/ej2-react-grids";
import { Tooltip } from "antd";
import {
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../../utilities/apiMethod";
import { message } from "antd";

const DOC_CONFIG = [
  { key: "PANDocument",            label: "PAN"   },
  { key: "GSTRegistrationDocument",label: "GST"   },
  { key: "CancelledCheque",        label: "Cheque"},
  { key: "FSSAICertificate",       label: "FSSAI" },
  { key: "MSMEDocument",           label: "MSME"  },
  { key: "TOTAgreement",           label: "TOT"   },
  { key: "PriceAndMargin",         label: "P&M"   },
];

const getFileIcon = (fileName) => {
  if (!fileName) return <FileOutlined />;
  const ext = fileName.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext))
    return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
  if (["xls", "xlsx"].includes(ext))
    return <FileExcelOutlined style={{ color: "#52c41a" }} />;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
    return <FileImageOutlined style={{ color: "#1890ff" }} />;
  return <FileOutlined style={{ color: "#8c8c8c" }} />;
};

const ROW_BG_EVEN = "#ffffff";
const ROW_BG_ODD  = "#f9f9f9";

const DocumentsCell = ({ data }) => {
  const [downloading, setDownloading] = useState({});

  const isAltRow = data.serialNo % 2 === 0;
  const rowBg = isAltRow ? ROW_BG_ODD : ROW_BG_EVEN;

  const handleDownload = async (fileName, label) => {
    if (!fileName || downloading[label]) return;
    try {
      setDownloading((prev) => ({ ...prev, [label]: true }));
      const response = await apiRequest(
        "GET",
        `FileUpload/blob/download?fileName=${fileName}`
      );
      const url = response?.Data;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        message.error("Could not retrieve download link");
      }
    } catch (err) {
      console.error("Download error:", err);
      message.error("Download failed");
    } finally {
      setDownloading((prev) => ({ ...prev, [label]: false }));
    }
  };

  const availableDocs = DOC_CONFIG.filter((d) => data[d.key]);

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        alignItems: "center",
        margin: "-3px -8px",
        padding: "4px 8px",
        minHeight: "100%",
        background: rowBg,
        borderTop: `1px solid #e8e8e8`,
      }}
    >
      {availableDocs.length === 0 ? (
        <span style={{ color: "#bfbfbf", fontSize: 11 }}>—</span>
      ) : (
        availableDocs.map(({ key, label }) => (
          <Tooltip key={key} title={`Download ${label}`} placement="top">
            <button
              onClick={() => handleDownload(data[key], label)}
              disabled={downloading[label]}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "2px 7px",
                fontSize: 11,
                fontWeight: 500,
                background: downloading[label] ? "#f5f5f5" : "#f0f7ff",
                border: "1px solid #91caff",
                borderRadius: 4,
                color: "#1677ff",
                cursor: downloading[label] ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.15s",
                lineHeight: "18px",
              }}
              onMouseEnter={(e) => {
                if (!downloading[label]) {
                  e.currentTarget.style.background = "#1677ff";
                  e.currentTarget.style.color = "#fff";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = downloading[label]
                  ? "#f5f5f5"
                  : "#f0f7ff";
                e.currentTarget.style.color = "#1677ff";
              }}
            >
              {downloading[label] ? (
                <DownloadOutlined spin />
              ) : (
                getFileIcon(data[key])
              )}
              {label}
            </button>
          </Tooltip>
        ))
      )}
    </div>
  );
};

const documentsTemplate = (data) => <DocumentsCell data={data} />;

export default function VendorGrid({ data = [] }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  let gridInstance;

  const toolbarClick = (args) => {
    if (
      gridInstance &&
      args.item.id === "Vendor_Audit_GridID_excelexport"
    ) {
      gridInstance.excelExport();
    }
  };

  const rowDataBound = (args) => {
    if (!args.row) return;
    const rowIndex = args.row.getAttribute("aria-rowindex");
    if (!rowIndex) return;
    const isAlt = parseInt(rowIndex) % 2 === 0;
    const siblingRows = document.querySelectorAll(
      `[aria-rowindex="${rowIndex}"]`
    );
    siblingRows.forEach((row) => {
      row.style.background = isAlt ? ROW_BG_ODD : ROW_BG_EVEN;
    });
  };

  return (
    <div
      className={`grid_compact ${isMobile ? "e-bigger" : ""}`}
      style={{
        position: "relative",
        marginTop: "10px",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        padding: "6px",
        marginBottom: "20px",
      }}
    >
      <Row>
        <Col>
          <GridComponent
            ref={(g) => (gridInstance = g)}
            id="Vendor_Audit_GridID"
            dataSource={data}
            allowPaging={true}
            allowSorting={true}
            allowFiltering={true}
            filterSettings={{ type: "Excel" }}
            toolbar={["ExcelExport", "Search"]}
            allowExcelExport={true}
            toolbarClick={toolbarClick}
            enableAdaptiveUI={isMobile}
            rowRenderingMode={isMobile ? "Vertical" : "Horizontal"}
            gridLines="Both"
            height={"400px"}
            pageSettings={{
              pageSizes: [10, 20, 30, 40, 50, "All"],
              pageCount: 10,
            }}
            rowHeight={isMobile ? "" : 36}
            frozenColumns={1}
            frozenRows={0}
            rowDataBound={rowDataBound}
          >
            <ColumnsDirective>

              {/* ── Identity ── */}
              <ColumnDirective
                field="serialNo"
                headerText="S No"
                width="105"          // 70 × 1.5
                textAlign="Center"
                isPrimaryKey={true}
                allowFiltering={false}
              />
              <ColumnDirective
                field="VendorNo"
                headerText="Vendor No"
                width="225"          // 150 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="VendorName"
                headerText="Vendor Name"
                width="300"          // 200 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="LegalName"
                headerText="Legal Name"
                width="300"          // 200 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="PANNumber"
                headerText="PAN Number"
                width="225"          // 150 × 1.5
                textAlign="Left"
              />

              {/* ── Business classification ── */}
              <ColumnDirective
                field="BusinessCategory"
                headerText="Business Category"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="VendorType"
                headerText="Vendor Type"
                width="195"          // 130 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="DivisionName"
                headerText="Division"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="BusinessType"
                headerText="Business Type"
                width="225"          // 150 × 1.5
                textAlign="Center"
              />

              {/* ── Contact ── */}
              <ColumnDirective
                field="EmailID"
                headerText="Email"
                width="330"          // 220 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="ContactPerson"
                headerText="Contact Person"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="ContactNumber"
                headerText="Contact No"
                width="225"          // 150 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="Location"
                headerText="Location"
                width="225"          // 150 × 1.5
                textAlign="Left"
              />

              {/* ── Addresses ── */}
              <ColumnDirective
                field="RegisteredAddress"
                headerText="Registered Address"
                width="450"          // 300 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="OrderAddress"
                headerText="Order Address"
                width="450"
                textAlign="Left"
              />
              <ColumnDirective
                field="InvoiceAddress"
                headerText="Invoice Address"
                width="450"
                textAlign="Left"
              />
              <ColumnDirective
                field="RemittanceAddress"
                headerText="Remittance Address"
                width="450"
                textAlign="Left"
              />
              <ColumnDirective
                field="ReturnAddress"
                headerText="Return Address"
                width="450"
                textAlign="Left"
              />
              <ColumnDirective
                field="AddressGST"
                headerText="GST Address"
                width="450"
                textAlign="Left"
              />

              {/* ── GST ── */}
              <ColumnDirective
                field="GSTRegistration"
                headerText="GST Reg."
                width="165"          // 110 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="GSTRegistrationNumber"
                headerText="GST Number"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />

              {/* ── MSME ── */}
              <ColumnDirective
                field="MSMERegistration"
                headerText="MSME Reg."
                width="165"          // 110 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="MSMERegistrationNumber"
                headerText="MSME Number"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />

              {/* ── FSSAI ── */}
              <ColumnDirective
                field="FSSAIRegistration"
                headerText="FSSAI Reg."
                width="165"          // 110 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="FSSAIRegistrationNumber"
                headerText="FSSAI Number"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />

              {/* ── Other ── */}
              <ColumnDirective
                field="Other"
                headerText="Other"
                width="180"
                textAlign="Left"
              />

              {/* ── Commercial terms ── */}
              <ColumnDirective
                field="AgreedCreditDays"
                headerText="Credit Days"
                width="180"          // 120 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="LeadTime"
                headerText="Lead Time"
                width="180"          // 120 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="DiscountPercent"
                headerText="Discount %"
                width="180"          // 120 × 1.5
                textAlign="Center"
              />
              <ColumnDirective
                field="PaymentMethod"
                headerText="Payment Method"
                width="300"          // 200 × 1.5
                textAlign="Left"
              />

              {/* ── Bank details ── */}
              <ColumnDirective
                field="BankName"
                headerText="Bank Name"
                width="270"          // 180 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="AccountNo"
                headerText="Account No"
                width="300"          // 200 × 1.5
                textAlign="Left"
              />
              <ColumnDirective
                field="IFSCCode"
                headerText="IFSC Code"
                width="225"          // 150 × 1.5
                textAlign="Left"
              />

              {/* ── Turnover ── */}
              <ColumnDirective
                field="Turnover_2023_24"
                headerText="Turnover 23-24"
                width="240"          // 160 × 1.5
                textAlign="Right"
                format="N0"
              />
              <ColumnDirective
                field="Turnover_2024_25"
                headerText="Turnover 24-25"
                width="240"          // 160 × 1.5
                textAlign="Right"
                format="N0"
              />
              <ColumnDirective
                field="Turnover_2025_26"
                headerText="Turnover 25-26"
                width="240"          // 160 × 1.5
                textAlign="Right"
                format="N0"
              />

              {/* ── Documents (frozen right) ── */}
              <ColumnDirective
                headerText="Documents"
                width="420"          // 280 × 1.5
                textAlign="Left"
                template={documentsTemplate}
                allowFiltering={false}
                allowSorting={false}
                freeze="Right"
              />

            </ColumnsDirective>
            <Inject
              services={[Page, Filter, Toolbar, ExcelExport, Sort, Group, Resize, Freeze]}
            />
          </GridComponent>
        </Col>
      </Row>
    </div>
  );
}